import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { Link } from "react-router-dom";
import { Utils } from "../utils";
import { Number } from "../controls";
import { BidStatusChip } from "./status";
import Confirm from "./Confirm";
import { useStore } from "../store";
import { useUpdateBid } from "../hooks/queries";

interface InputProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  data: any;
}

const BidUpdatePopup: React.FC<InputProps> = ({
  show,
  onHide,
  onSuccess,
  data,
}) => {
  const [price, setPrice] = useState<string>();
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { setIsLoading, setErrorMessage, setSuccessMessage } = useStore(
    (s: any) => s,
  );
  const updateBid = useUpdateBid();

  const renderPercentageChange = (row: any) => {
    return Utils.formatPercentChange(row?.adId?.price, row?.price, 2);
  };

  const handleClose = () => {
    setValidationMessage("");
    setPrice("");
    setAcceptedTerms(false);
    onHide();
  };

  const handleSubmitClick = () => {
    if (!data || !data?._id) return;
    if (!price) return setValidationMessage("Please provide a valid price.");
    if (parseFloat(price) < parseFloat(data?.adId?.price))
      return setValidationMessage(
        "Your bid must be higher than the opening bid price.",
      );
    if (parseFloat(price) <= parseFloat(data?.price))
      return setValidationMessage(
        "Your new bid price must be higher than your previous bid price.",
      );

    setValidationMessage("");
    setShowConfirmModal(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowConfirmModal(false);
    if (!data || !data?._id || !price) return;

    setIsLoading(true);
    try {
      await updateBid.mutateAsync({
        bidId: data?._id,
        price: price as string,
        adId: data?.adId?._id,
      });
    } catch (err: any) {
      setErrorMessage(err?.message || "Request failed");
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setSuccessMessage(
      "Your bid has been updated. We'll email you if you win the bid.",
    );
    onHide();
    setValidationMessage("");
    setPrice("");
    setAcceptedTerms(false);
    onSuccess();
  };

  const minBid = Math.max(
    parseFloat(data?.price || "0"),
    parseFloat(data?.adId?.price || "0"),
  );

  const isPriceValid =
    !!price &&
    parseFloat(price) >= parseFloat(data?.adId?.price || "0") &&
    parseFloat(price) > parseFloat(data?.price || "0");
  const canSubmit = isPriceValid && acceptedTerms;

  return (
    <>
      <Modal
        className="placeBidModal bidUpdateModal"
        size="lg"
        show={show}
        onHide={onHide}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton className="placeBidModal__header">
          <Modal.Title className="placeBidModal__heading">
            Update your bid
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="placeBidModal__body">
          <div className="adDetails__panel">
            <div className="bidUpdateModal__titleRow">
              <h1 className="adDetails__title">{data?.adId?.title}</h1>
              <BidStatusChip status={data?.status} />
            </div>

            <div className="adDetails__metaGrid">
              <div className="adDetails__metaTile">
                <div className="adDetails__metaLabel">Your bid</div>
                <div className="adDetails__metaValue">
                  {Utils.formatToCurrency(data?.price)}
                </div>
              </div>
              <div className="adDetails__metaTile">
                <div className="adDetails__metaLabel">Opening bid</div>
                <div className="adDetails__metaValue">
                  {Utils.formatToCurrency(data?.adId?.price)}
                </div>
              </div>
              <div className="adDetails__metaTile">
                <div className="adDetails__metaLabel">% Increase</div>
                <div className="adDetails__metaValue">
                  {renderPercentageChange(data)}
                </div>
              </div>
              <div className="adDetails__metaTile">
                <div className="adDetails__metaLabel">Time left</div>
                <div className="adDetails__metaValue">
                  {Utils.formatFromNow(data?.adId?.biddingEnds)}
                </div>
              </div>
              <div className="adDetails__metaTile">
                <div className="adDetails__metaLabel">Listed</div>
                <div className="adDetails__metaValue">
                  {Utils.formatDate(data?.adId?.createdAt)}
                </div>
              </div>
              <div className="adDetails__metaTile">
                <div className="adDetails__metaLabel">Bid placed</div>
                <div className="adDetails__metaValue">
                  {Utils.formatDateTime(data?.createdAt)}
                </div>
              </div>
            </div>

            <div className="placeBidModal__info">
              <i className="bi bi-info-circle" aria-hidden="true" />
              <div>
                Heads up: If you win, additional fees apply on top of your bid.{" "}
                <strong>Arta</strong> handles shipping and is added at checkout.{" "}
                <strong>Trustap</strong>, our payment partner, applies a buyer
                protection fee as follows:
                <ul className="placeBidModal__feeList">
                  <li>
                    <strong>5%</strong> of purchase price on credit card
                    payments (immediately processed)
                  </li>
                  <li>
                    <strong>1.5%</strong> of purchase price on bank transfers
                    (takes up to 3&ndash;4 business days)
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="placeBidModal__bidSection">
            <div className="placeBidModal__bidLabel">
              <i className="bi bi-hammer" aria-hidden="true" />
              <span>New bid price</span>
            </div>
            <div className="placeBidModal__bidInputWrap">
              <span className="placeBidModal__currencyPrefix">$</span>
              <Number
                placeholder="Enter your new bid"
                name="bidPrice"
                value={price}
                onChange={(e: any) => setPrice(e?.target?.value)}
                thousandSeparator={true}
              />
            </div>
            <p className="placeBidModal__bidHint">
              Must be higher than your current bid (
              {Utils.formatToCurrency(minBid)}).
            </p>
          </div>

          {validationMessage && (
            <div className="placeBidModal__error">
              <i className="bi bi-x-circle" aria-hidden="true" />
              <span>{validationMessage}</span>
            </div>
          )}

          <Form.Check
            type="checkbox"
            id="updateBidAcceptTerms"
            className="termsCheck mt-3"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            label={
              <>
                I have read and agree to the{" "}
                <Link
                  to="/bidding-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Bidding Terms &amp; Conditions
                </Link>
              </>
            }
          />
        </Modal.Body>
        <Modal.Footer className="placeBidModal__footer">
          <button
            type="button"
            className="placeBidModal__cancel"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="placeBidModal__submit"
            onClick={handleSubmitClick}
            disabled={!canSubmit}
          >
            <i className="bi bi-arrow-up-circle" aria-hidden="true" />
            <span>Update Bid</span>
          </button>
        </Modal.Footer>
      </Modal>
      <Confirm
        show={showConfirmModal}
        variant="primary"
        iconClass="bi-arrow-up-circle"
        title="Update this bid?"
        body={`Are you sure you want to raise your bid from ${Utils.formatToCurrency(
          data?.price,
        )} to ${Utils.formatToCurrency(price)} for "${
          data?.adId?.title
        }"? Please note: you won't be able to change this bid to a lower amount later.`}
        closeButtonText="Go Back"
        confirmButtonText="Yes, Update Bid"
        onNo={() => setShowConfirmModal(false)}
        onYes={handleConfirmedSubmit}
      />
    </>
  );
};

export default BidUpdatePopup;
